import React, { useMemo, useState } from 'react';

function RegisterForm({ event, existingRegistration, onSignupSuccess }) {
    const [formData, setFormData] = useState({
        style: '',
        weight: ''
    });
    const [loading, setLoading] = useState(false);
    const [cancelLoading, setCancelLoading] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    const registrationStatus = existingRegistration?.status || null;

    const isActiveRegistration = useMemo(() => {
        return ['pending', 'approved', 'waitlisted', 'checked_in'].includes(registrationStatus);
    }, [registrationStatus]);

    const canSelfCancel = useMemo(() => {
        return ['pending', 'approved', 'waitlisted'].includes(registrationStatus);
    }, [registrationStatus]);

    const statusLabel = useMemo(() => {
        if (!registrationStatus) return '';
        return String(registrationStatus).replace('_', ' ').toUpperCase();
    }, [registrationStatus]);

    const previouslyCancelled = registrationStatus === 'cancelled';
    const previouslyRejected = registrationStatus === 'rejected';

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!event?._id) {
            setError('No event selected.');
            return;
        }

        setLoading(true);
        setError('');
        setSuccessMessage('');

        const answers = {};

        if (formData.style.trim()) {
            answers.style = formData.style.trim();
        }

        if (formData.weight !== '') {
            const weightValue = Number(formData.weight);
            if (!Number.isFinite(weightValue) || weightValue <= 0) {
                setLoading(false);
                setError('Weight must be a positive number.');
                return;
            }
            answers.weight = weightValue;
        }

        try {
            const response = await fetch(`/api/events/${event._id}/register`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ answers })
            });

            const data = await response.json().catch(() => null);

            if (!response.ok) {
                setError(data?.message || data?.error || 'Registration failed.');
                return;
            }

            const status = data?.registration?.status || 'submitted';

            if (status === 'approved') {
                setSuccessMessage('Your registration is approved.');
            } else if (status === 'pending') {
                setSuccessMessage('Your registration is pending admin approval.');
            } else if (status === 'waitlisted') {
                setSuccessMessage('You have been added to the waitlist.');
            } else {
                setSuccessMessage('Registration submitted.');
            }

            if (typeof onSignupSuccess === 'function') {
                await onSignupSuccess();
            }
        } catch (err) {
            console.error('Registration error:', err);
            setError('Registration failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    async function handleCancelRegistration() {
        if (!existingRegistration?._id) return;

        setCancelLoading(true);
        setError('');
        setSuccessMessage('');

        try {
            const response = await fetch(`/api/registrations/self/${existingRegistration._id}/cancel`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            const data = await response.json().catch(() => null);

            if (!response.ok) {
                setError(data?.message || data?.error || 'Unable to cancel registration.');
                return;
            }

            setSuccessMessage('Your registration has been cancelled.');

            if (typeof onSignupSuccess === 'function') {
                await onSignupSuccess();
            }
        } catch (err) {
            console.error('Cancel registration error:', err);
            setError('Unable to cancel registration.');
        } finally {
            setCancelLoading(false);
        }
    }

    if (!event) {
        return (
            <div className="register-form">
                <div className="status-msg" style={{ color: '#888' }}>
                    No event selected.
                </div>
            </div>
        );
    }

    if (isActiveRegistration) {
        return (
            <div className="register-form">
                <div style={{ textAlign: 'left', marginBottom: '1rem' }}>
                    <div style={{ fontWeight: 'bold', marginBottom: '0.4rem' }}>{event.title}</div>
                    <div style={{ color: '#999', fontSize: '0.9rem' }}>
                        Registration status: {statusLabel}
                    </div>
                </div>

                {error && (
                    <div style={{ color: '#ff6b6b', fontSize: '0.9rem', marginBottom: '0.8rem' }}>
                        {error}
                    </div>
                )}

                {successMessage && (
                    <div style={{ color: '#7bd88f', fontSize: '0.9rem', marginBottom: '0.8rem' }}>
                        {successMessage}
                    </div>
                )}

                <div className="success-zone">
                    <p
                        className="status-msg"
                        style={{
                            marginBottom: '0.8rem',
                            color: '#888',
                            fontSize: '0.8rem',
                            textTransform: 'uppercase'
                        }}
                    >
                        You are currently registered for this event.
                    </p>
                </div>

                {canSelfCancel && (
                    <button
                        type="button"
                        onClick={handleCancelRegistration}
                        disabled={cancelLoading}
                    >
                        {cancelLoading ? 'CANCELLING...' : 'Cancel Registration'}
                    </button>
                )}
            </div>
        );
    }

    return (
        <form className="register-form" onSubmit={handleSubmit}>
            <div style={{ textAlign: 'left', marginBottom: '1rem' }}>
                <div style={{ fontWeight: 'bold', marginBottom: '0.4rem' }}>{event.title}</div>
                <div style={{ color: '#999', fontSize: '0.9rem' }}>
                    {event.approvalRequired ? 'Approval required' : 'Instant confirmation if capacity is available'}
                </div>

                {previouslyCancelled && (
                    <div style={{ color: '#999', fontSize: '0.88rem', marginTop: '0.5rem' }}>
                        Your previous registration was cancelled. You can join again below.
                    </div>
                )}

                {previouslyRejected && (
                    <div style={{ color: '#999', fontSize: '0.88rem', marginTop: '0.5rem' }}>
                        Your previous registration was rejected. You can try again if the event is still open.
                    </div>
                )}
            </div>

            {event.registrationForm?.requireStyle && (
                <input
                    type="text"
                    placeholder="Style (e.g. Kyokushin)"
                    required
                    value={formData.style}
                    onChange={(e) => setFormData({ ...formData, style: e.target.value })}
                />
            )}

            {event.registrationForm?.requireWeight && (
                <input
                    type="number"
                    placeholder="Weight (kg)"
                    required
                    value={formData.weight}
                    onChange={(e) => setFormData({ ...formData, weight: e.target.value })}
                />
            )}

            {!event.registrationForm?.requireStyle && (
                <input
                    type="text"
                    placeholder="Style (optional)"
                    value={formData.style}
                    onChange={(e) => setFormData({ ...formData, style: e.target.value })}
                />
            )}

            {!event.registrationForm?.requireWeight && (
                <input
                    type="number"
                    placeholder="Weight (optional)"
                    value={formData.weight}
                    onChange={(e) => setFormData({ ...formData, weight: e.target.value })}
                />
            )}

            {error && (
                <div style={{ color: '#ff6b6b', fontSize: '0.9rem', marginBottom: '0.8rem' }}>
                    {error}
                </div>
            )}

            {successMessage && (
                <div style={{ color: '#7bd88f', fontSize: '0.9rem', marginBottom: '0.8rem' }}>
                    {successMessage}
                </div>
            )}

            <button type="submit" disabled={loading}>
                {loading ? 'PROCESSING...' : 'Confirm Entry'}
            </button>
        </form>
    );
}

export default RegisterForm;